import { getAuthHeaders } from '../pages/assets';
import { accessTokenSignal } from '../store/auth';
import { escapeHtml } from './sanitize';

const CLIENT_VERSION = import.meta.env.VITE_APP_VERSION?.trim() || '1.0.0';
const UPDATE_CONTAINER_ID = 'app-update-container';

type AppUpdateMode = 'available' | 'required' | 'activating';

let _serviceWorkerRegistration: ServiceWorkerRegistration | null = null;
let _updateMode: AppUpdateMode | null = null;
let _reloadForUpdate = false;
let _reloadFallback: ReturnType<typeof setTimeout> | null = null;
let _updateLifecycleBound = false;

function parseStableVersion(value: string): readonly [number, number, number] | null {
  const match = /^(\d+)\.(\d+)\.(\d+)$/.exec(value.trim());
  if (!match) return null;
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

export function isClientVersionOutdated(current: string, minimum: string): boolean {
  const currentParts = parseStableVersion(current);
  const minimumParts = parseStableVersion(minimum);
  if (!currentParts || !minimumParts) return false;

  for (let index = 0; index < currentParts.length; index += 1) {
    const currentPart = currentParts[index]!;
    const minimumPart = minimumParts[index]!;
    if (currentPart !== minimumPart) {
      return currentPart < minimumPart;
    }
  }
  return false;
}

function setApplicationInert(inert: boolean): void {
  const app = document.getElementById('app');
  if (inert) app?.setAttribute('inert', '');
  else app?.removeAttribute('inert');
}

function renderUpdateNotice(mode: AppUpdateMode, minimumVersion?: string, detail?: string): void {
  const container = document.getElementById(UPDATE_CONTAINER_ID);
  if (!container) return;

  _updateMode = mode;
  const mandatory = mode !== 'available';
  setApplicationInert(mandatory);

  const heading = mode === 'available'
    ? 'Application update ready'
    : mode === 'activating' ? 'Installing application update' : 'Application update required';
  const message = detail || (mode === 'available'
    ? 'Install the latest version when you are ready.'
    : mode === 'activating' ? 'Keep this app open while the update is applied.'
      : `Version ${minimumVersion || 'newer'} is required before work can continue.`);
  const buttonLabel = mode === 'activating' ? 'Updating…' : mode === 'available' ? 'Update now' : 'Retry update';

  container.innerHTML = `
    <s-banner tone="${mode === 'available' ? 'info' : 'critical'}" heading="${heading}">
      <s-stack gap="small">
        <s-paragraph>${escapeHtml(message)}</s-paragraph>
        <s-button id="apply-app-update" variant="primary" ${mode === 'activating' ? 'disabled' : ''}>${buttonLabel}</s-button>
      </s-stack>
    </s-banner>`;

  container.querySelector('#apply-app-update')?.addEventListener('click', () => {
    void activateAppUpdate(minimumVersion);
  });
}

function showUpdateAvailable(): void {
  if (_updateMode === 'required' || _updateMode === 'activating') return;
  renderUpdateNotice('available');
}

function clearUpdateNotice(): void {
  document.getElementById(UPDATE_CONTAINER_ID)?.replaceChildren();
  setApplicationInert(false);
  _updateMode = null;
}

async function checkMinimumClientVersion(): Promise<void> {
  try {
    const response = await fetch('/api/v1/health', {
      cache: 'no-store',
      headers: { 'X-OPM-Client-Version': CLIENT_VERSION },
    });
    if (!response.ok) return;

    const payload = await response.json() as { minimumClientVersion?: unknown; version?: unknown };
    const minimum = typeof payload.minimumClientVersion === 'string'
      ? payload.minimumClientVersion.trim() : '';
    if (minimum && isClientVersionOutdated(CLIENT_VERSION, minimum)) {
      renderUpdateNotice('required', minimum);
      return;
    }

    if (_updateMode === 'required') {
      clearUpdateNotice();
    }

    const releaseAvailable = typeof payload.version === 'string'
      && isClientVersionOutdated(CLIENT_VERSION, payload.version);
    if (releaseAvailable) {
      showUpdateAvailable();
    } else if (_updateMode === 'available') {
      clearUpdateNotice();
    }
  } catch (err) {
    console.warn('PWA: Client version check deferred:', err);
  }
}

function bindUpdateLifecycle(registration: ServiceWorkerRegistration): void {
  if (registration.waiting && navigator.serviceWorker.controller) showUpdateAvailable();

  registration.addEventListener('updatefound', () => {
    const installingWorker = registration.installing;
    if (!installingWorker) return;
    installingWorker.addEventListener('statechange', () => {
      if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
        showUpdateAvailable();
      }
    });
  });

  if (_updateLifecycleBound) return;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!_reloadForUpdate) return;
    _reloadForUpdate = false;
    if (_reloadFallback) clearTimeout(_reloadFallback);
    window.location.reload();
  });
  _updateLifecycleBound = true;
}

function waitForWaitingWorker(registration: ServiceWorkerRegistration): Promise<ServiceWorker | null> {
  if (registration.waiting) return Promise.resolve(registration.waiting);
  if (!registration.installing) return Promise.resolve(null);

  return new Promise((resolve) => {
    const interval = setInterval(() => {
      if (!registration.waiting) return;
      clearInterval(interval);
      clearTimeout(timeout);
      resolve(registration.waiting);
    }, 100);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      resolve(null);
    }, 8000);
  });
}

export async function activateAppUpdate(minimumVersion?: string): Promise<void> {
  const mandatory = _updateMode === 'required' || !!minimumVersion;
  renderUpdateNotice('activating', minimumVersion);

  try {
    const registration = _serviceWorkerRegistration || await navigator.serviceWorker.getRegistration();
    if (!registration) {
      window.location.reload();
      return;
    }

    await registration.update();
    const waitingWorker = await waitForWaitingWorker(registration);
    if (!waitingWorker) {
      window.location.reload();
      return;
    }

    _reloadForUpdate = true;
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    _reloadFallback = setTimeout(() => window.location.reload(), 5000);
  } catch (err) {
    console.error('PWA: Application update failed:', err);
    renderUpdateNotice(mandatory ? 'required' : 'available', minimumVersion, 'The update could not be installed. Check the connection and retry.');
  }
}

async function checkForApplicationUpdate(): Promise<void> {
  void checkMinimumClientVersion();
  if (!_serviceWorkerRegistration) return;
  try {
    await _serviceWorkerRegistration.update();
  } catch (err) {
    console.warn('PWA: Service worker update check deferred:', err);
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function applicationServerKeyMatches(subscription: PushSubscription, expectedKey: Uint8Array): boolean {
  const configuredKey = subscription.options.applicationServerKey;
  if (!configuredKey) {
    return false;
  }

  const actualKey = new Uint8Array(configuredKey);
  if (actualKey.length !== expectedKey.length) {
    return false;
  }

  return actualKey.every((value, index) => value === expectedKey[index]);
}

async function fetchVapidPublicKey(): Promise<Uint8Array> {
  const response = await fetch('/api/v1/notifications/vapid-public-key', {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch VAPID public key (${response.status})`);
  }

  const payload = await response.json() as unknown;
  if (!payload || typeof payload !== 'object' || !('publicKey' in payload)) {
    throw new Error('Server returned an invalid VAPID public key response');
  }

  const publicKey = (payload as { publicKey?: unknown }).publicKey;
  if (typeof publicKey !== 'string' || publicKey.trim() === '') {
    throw new Error('VAPID public key is not configured on the server');
  }

  const keyBytes = urlBase64ToUint8Array(publicKey.trim());
  if (keyBytes.length === 0) {
    throw new Error('VAPID public key on server decoded to empty bytes');
  }

  return keyBytes;
}

export function flushOfflineNotificationEvents() {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'FLUSH_NOTIFICATION_EVENTS' });
  }
}

let _maintenanceTimer: ReturnType<typeof setInterval> | null = null;
let _subscriptionChangeListenerBound = false;
let _lifecycleListenersBound = false;

interface PushSubscriptionChangedMessage {
  type: 'PUSH_SUBSCRIPTION_CHANGED';
  subscription: PushSubscriptionJSON;
  oldEndpoint?: string;
}

function isPushSubscriptionChangedMessage(value: unknown): value is PushSubscriptionChangedMessage {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<PushSubscriptionChangedMessage>;
  return candidate.type === 'PUSH_SUBSCRIPTION_CHANGED'
    && !!candidate.subscription
    && typeof candidate.subscription.endpoint === 'string';
}

async function persistRotatedSubscription(message: PushSubscriptionChangedMessage): Promise<void> {
  const persisted = await sendSubscriptionToBackend(message.subscription);
  if (!persisted || !message.oldEndpoint || message.oldEndpoint === message.subscription.endpoint) return;
  await removeSubscriptionFromBackend(message.oldEndpoint);
}

export async function maintainPushSubscription(): Promise<boolean> {
  if (!accessTokenSignal.value) {
    return false;
  }
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return false;
  }

  if (Notification.permission !== 'granted') {
    return false;
  }

  try {
    const reg = await navigator.serviceWorker.ready;
    const applicationServerKey = await fetchVapidPublicKey();
    let subscription = await reg.pushManager.getSubscription();

    if (subscription && applicationServerKeyMatches(subscription, applicationServerKey)) {
      return await sendSubscriptionToBackend(subscription);
    }

    if (subscription) {
      await removeSubscriptionFromBackend(subscription.endpoint);
      await subscription.unsubscribe();
      subscription = null;
    }

    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey as BufferSource,
    });

    return await sendSubscriptionToBackend(subscription);
  } catch (err) {
    console.warn('PWA: Push subscription maintenance check deferred:', err);
    return false;
  }
}

export function startPushSubscriptionMaintenanceLoop(): void {
  void maintainPushSubscription();

  if (_maintenanceTimer) {
    clearInterval(_maintenanceTimer);
  }

  // Regular subscription verification & maintenance loop every 3 minutes
  _maintenanceTimer = setInterval(() => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      void maintainPushSubscription();
    }
  }, 3 * 60 * 1000);
}

export function stopPushSubscriptionMaintenanceLoop(): void {
  if (_maintenanceTimer) {
    clearInterval(_maintenanceTimer);
    _maintenanceTimer = null;
  }
}

export async function registerServiceWorker() {
  void checkMinimumClientVersion();

  if (!('serviceWorker' in navigator)) {
    console.warn('Service Worker is not supported by this browser.');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      updateViaCache: 'none',
    });
    _serviceWorkerRegistration = registration;
    bindUpdateLifecycle(registration);
    await registration.update();
    if (registration.waiting && navigator.serviceWorker.controller) showUpdateAvailable();
    console.log('Service Worker registered successfully with scope:', registration.scope);

    // Register Web Periodic Background Sync if supported
    if ('periodicSync' in registration) {
      try {
        await (registration as any).periodicSync.register('check_push_subscription', {
          minInterval: 12 * 60 * 60 * 1000,
        });
      } catch (e) {
        // Periodic sync permission not granted or unsupported
      }
    }

    // Flush offline notification events and maintain push subscription automatically
    flushOfflineNotificationEvents();
    startPushSubscriptionMaintenanceLoop();

    if (!_subscriptionChangeListenerBound) {
      navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
        if (isPushSubscriptionChangedMessage(event.data)) {
          void persistRotatedSubscription(event.data);
        }
      });
      _subscriptionChangeListenerBound = true;
    }

    if (!_lifecycleListenersBound) {
      window.addEventListener('online', () => {
        flushOfflineNotificationEvents();
        void maintainPushSubscription();
        void checkForApplicationUpdate();
      });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          flushOfflineNotificationEvents();
          void maintainPushSubscription();
          void checkForApplicationUpdate();
        }
      });
      _lifecycleListenersBound = true;
    }
  } catch (err) {
    console.error('Service Worker registration failed:', err);
  }
}

export async function getPushSubscriptionStatus(): Promise<'supported' | 'unsupported' | 'subscribed' | 'unsubscribed' | 'denied'> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return 'unsupported';
  }
  if ('Notification' in window && Notification.permission === 'denied') {
    return 'denied';
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    const subscription = await reg.pushManager.getSubscription();
    if (!subscription) return 'unsubscribed';
    try {
      const response = await fetch('/api/v1/notifications/status', { headers: getAuthHeaders() });
      if (response.ok) {
        const payload = await response.json() as { backendRegistered?: unknown };
        if (payload.backendRegistered !== true) return 'unsubscribed';
      }
    } catch {
      // The local subscription remains useful while the status endpoint is unavailable.
    }
    return 'subscribed';
  } catch (e) {
    return 'unsupported';
  }
}

export async function subscribeUserToPush(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return false;
  }

  try {
    if (!('Notification' in window)) {
      return false;
    }

    const permission = Notification.permission === 'default'
      ? await Notification.requestPermission()
      : Notification.permission;

    if (permission !== 'granted') {
      console.warn('Notification permission denied.');
      return false;
    }

    const reg = await navigator.serviceWorker.ready;
    const applicationServerKey = await fetchVapidPublicKey();
    let subscription = await reg.pushManager.getSubscription();

    if (subscription && applicationServerKeyMatches(subscription, applicationServerKey)) {
      if (await sendSubscriptionToBackend(subscription)) {
        return true;
      }
    }

    if (subscription) {
      await removeSubscriptionFromBackend(subscription.endpoint);
      await subscription.unsubscribe();
      subscription = null;
    }

    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey as BufferSource,
    });

    if (await sendSubscriptionToBackend(subscription)) {
      return true;
    }

    await subscription.unsubscribe();
    return false;
  } catch (err) {
    console.error('Failed to subscribe user to push notifications:', err);
    return false;
  }
}

async function sendSubscriptionToBackend(subscription: PushSubscription | PushSubscriptionJSON): Promise<boolean> {
  if (!accessTokenSignal.value) {
    return false;
  }
  const rawSub = 'toJSON' in subscription ? subscription.toJSON() : subscription;
  if (!rawSub.endpoint || !rawSub.keys || !rawSub.keys.auth || !rawSub.keys.p256dh) {
    console.error('Push Subscription is missing required fields');
    return false;
  }

  const res = await fetch('/api/v1/notifications/subscribe', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(rawSub),
  });

  if (!res.ok) {
    console.error(`Failed to save push subscription on backend (${res.status})`);
    return false;
  }

  console.log('Push subscription saved successfully on backend');
  return true;
}

async function removeSubscriptionFromBackend(endpoint: string): Promise<boolean> {
  try {
    const response = await fetch('/api/v1/notifications/unsubscribe', {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ endpoint }),
    });

    if (!response.ok) {
      console.warn(`Failed to remove push subscription on backend (${response.status})`);
    }
    return response.ok;
  } catch (error) {
    console.warn('Failed to contact backend while removing push subscription', error);
    return false;
  }
}

export async function unsubscribeUserFromPush(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;

  try {
    const reg = await navigator.serviceWorker.ready;
    const subscription = await reg.pushManager.getSubscription();
    if (!subscription) return true;

    const backendRemoved = await removeSubscriptionFromBackend(subscription.endpoint);
    const locallyRemoved = await subscription.unsubscribe();
    if (!backendRemoved) {
      console.warn('Push subscription was removed locally; backend cleanup will rely on endpoint expiry.');
    }
    if (!locallyRemoved) {
      return false;
    }

    console.log('Push subscription removed successfully');
    return true;
  } catch (err) {
    console.error('Error during push unsubscription:', err);
    return false;
  }
}
